import {
  ChangeDetectionStrategy,
  Component,
  inject,
  Input,
  signal,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { LucideAngularModule, BookOpen, Check, X } from 'lucide-angular';
import {
  ScoringItemPreview,
  ScoringCriteriaPreview,
  ScoringCriteriaLevel,
  ScoringGuideStandard,
} from '../../../../grading.types';
import { AmplitudeService } from '../../../../../../../common/analytics/amplitude.service';
import {
  StandardInfoModalComponent,
  StandardInfoData,
} from '../standard-info-modal/standard-info-modal';

@Component({
  selector: 'app-scoring-guide-table',
  standalone: true,
  imports: [CommonModule, LucideAngularModule, StandardInfoModalComponent],
  templateUrl: './scoring-guide-table.html',
  styleUrl: './scoring-guide-table.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ScoringGuideTableComponent {
  private readonly amplitudeService = inject(AmplitudeService);

  @Input({ required: true }) item!: ScoringItemPreview;

  /** Full standards data for modal display. If empty, badge clicks are no-ops. */
  @Input() standards: ScoringGuideStandard[] = [];

  /** Currently selected standard for the modal */
  protected readonly selectedStandard = signal<StandardInfoData | null>(null);

  protected readonly icons = { BookOpen, Check, X };

  /**
   * Get the maximum number of levels across all criteria
   */
  protected get maxLevels(): number {
    return Math.max(...this.item.criteria.map((c) => c.levels.length), 0);
  }

  /**
   * Get level indices array for column headers
   */
  protected get levelIndices(): number[] {
    return Array.from({ length: this.maxLevels }, (_, i) => i);
  }

  /**
   * Check if this is an exact_match type (2 levels: incorrect/correct)
   */
  protected get isExactMatch(): boolean {
    return this.item.type === 'exact_match';
  }

  /**
   * Check if this is a single criteria item (rubric or logic with 1 criteria)
   */
  protected get isSingleCriteria(): boolean {
    return this.item.criteria.length === 1;
  }

  /**
   * Get level label based on position and total levels
   */
  protected getLevelLabel(levelIndex: number): string {
    const totalLevels = this.maxLevels;
    if (totalLevels === 2) {
      return levelIndex === 0 ? 'Incorrect' : 'Correct';
    }
    const labels = ['Beginning', 'Approaching', 'Meets', 'Exceeds'];
    return labels[levelIndex] || `Level ${levelIndex + 1}`;
  }

  /**
   * Get pastel background color class for a level
   */
  protected getLevelBgClass(levelIndex: number): string {
    const totalLevels = this.maxLevels;
    if (totalLevels === 2) {
      return levelIndex === 0 ? 'level-incorrect' : 'level-correct';
    }
    const classes = [
      'level-beginning',
      'level-approaching',
      'level-meets',
      'level-exceeds',
    ];
    return classes[levelIndex] || '';
  }

  /**
   * Get text color class for level header
   */
  protected getLevelTextClass(levelIndex: number): string {
    const totalLevels = this.maxLevels;
    if (totalLevels === 2) {
      return levelIndex === 0 ? 'text-red-600' : 'text-green-600';
    }
    const classes = [
      'text-red-600',
      'text-orange-600',
      'text-blue-600',
      'text-green-600',
    ];
    return classes[levelIndex] || 'text-gray-600';
  }

  /**
   * Get max points for a criteria (highest level's points)
   */
  protected getCriteriaMaxPoints(criteria: ScoringCriteriaPreview): number {
    if (!criteria.levels || criteria.levels.length === 0) return 0;
    return criteria.levels[criteria.levels.length - 1].points;
  }

  /**
   * Handle standard badge click — look up the full standard and show the modal.
   */
  protected onShowStandard(criteria: ScoringCriteriaPreview): void {
    if (!criteria.standard) return;

    this.amplitudeService.trackEvent('grading_standard_badge_clicked', {
      standardId: criteria.standard.standardId,
      criteriaName: criteria.name,
    });

    // Look up the full standard text from the provided standards list
    const fullStandard = this.standards.find(
      (s) => s.code === criteria.standard!.standardId,
    );

    this.selectedStandard.set({
      code: fullStandard?.code ?? criteria.standard.standardId,
      text: fullStandard?.text ?? 'Standard details not available.',
      alignmentRationale: criteria.standard.alignmentRationale,
      criteriaName: criteria.name,
    });
  }

  /**
   * Close the standard details modal.
   */
  protected closeStandardModal(): void {
    this.selectedStandard.set(null);
  }

  /**
   * Check if a level's description is not just the default "Incorrect"/"Correct"
   */
  protected hasNonDefaultDescription(
    level: ScoringCriteriaLevel,
    index: number,
  ): boolean {
    const defaultDescriptions = ['incorrect', 'correct'];
    const desc = level.description?.toLowerCase().trim() || '';
    return desc !== '' && desc !== defaultDescriptions[index];
  }

  /**
   * Check if any level in criteria has a meaningful non-default description
   */
  protected hasAnyNonDefaultDescription(
    criteria: ScoringCriteriaPreview,
  ): boolean {
    return criteria.levels.some((level, index) =>
      this.hasNonDefaultDescription(level, index),
    );
  }
}
